import type { RouteLocationNormalizedLoaded } from 'vue-router';

import { useLogger } from '~/composables/useLogger';
import { SESSION_INACTIVITY_TTL_MS } from '~/composables/visitorIdentity/utils';
import { isClient } from '~/utils';

export const LAST_SRP_URL_STORAGE_KEY = 'lastSrpUrl';
export const LAST_SRP_ACTIVITY_STORAGE_KEY = 'lastSrpUrlLastActivity';
export const LAST_SRP_INACTIVITY_TTL_MS = SESSION_INACTIVITY_TTL_MS;

const logger = useLogger('lastSrpUrl');

type StorageOperation = 'clear' | 'read' | 'persist' | 'refresh activity';

function getCurrentTimestamp(): number {
	return Date.now();
}

function reportStorageError(
	operation: StorageOperation,
	error: unknown,
	fallbackAction: string
): void {
	logger.error('Last SRP URL localStorage operation failed', {
		operation,
		fallback_action: fallbackAction,
		storage_key: LAST_SRP_URL_STORAGE_KEY,
		activity_storage_key: LAST_SRP_ACTIVITY_STORAGE_KEY,
		error
	});
}

function handleReadStorageError(error: unknown): null {
	reportStorageError('read', error, 'use_neighborhood_srp_fallback');
	return null;
}

function readLastActivity(): number | null {
	const raw = localStorage.getItem(LAST_SRP_ACTIVITY_STORAGE_KEY);
	if (!raw) return null;

	const timestamp = Number(raw);
	return Number.isFinite(timestamp) ? timestamp : null;
}

function isExpired(lastActivity: number): boolean {
	return getCurrentTimestamp() - lastActivity > LAST_SRP_INACTIVITY_TTL_MS;
}

function getActiveLastSrpUrl(): string | null {
	const url = localStorage.getItem(LAST_SRP_URL_STORAGE_KEY);
	const lastActivity = readLastActivity();

	if (!url || !lastActivity || isExpired(lastActivity)) {
		clearLastSrpUrlFromStorage();
		return null;
	}

	return url;
}

export function clearLastSrpUrlFromStorage(): void {
	if (!isClient()) return;

	try {
		localStorage.removeItem(LAST_SRP_URL_STORAGE_KEY);
		localStorage.removeItem(LAST_SRP_ACTIVITY_STORAGE_KEY);
	} catch (error) {
		reportStorageError('clear', error, 'skip_storage_clear');
	}
}

export function getLastSrpUrlFromStorage(): string | null {
	if (!isClient()) return null;

	try {
		return getActiveLastSrpUrl();
	} catch (error) {
		return handleReadStorageError(error);
	}
}

export function hasLastSrpUrlInStorage(): boolean {
	return !!getLastSrpUrlFromStorage();
}

export function setLastSrpUrlInStorage(url: string): void {
	if (!isClient() || !url) return;

	try {
		localStorage.setItem(LAST_SRP_URL_STORAGE_KEY, url);
		localStorage.setItem(
			LAST_SRP_ACTIVITY_STORAGE_KEY,
			String(getCurrentTimestamp())
		);
	} catch (error) {
		reportStorageError('persist', error, 'skip_last_srp_persistence');
	}
}

export function refreshLastSrpUrlActivity(): void {
	if (!isClient()) return;

	try {
		if (!getActiveLastSrpUrl()) return;

		localStorage.setItem(
			LAST_SRP_ACTIVITY_STORAGE_KEY,
			String(getCurrentTimestamp())
		);
	} catch (error) {
		reportStorageError('refresh activity', error, 'keep_existing_activity');
	}
}

export function captureLastSrpUrlFromRoute(
	route: Pick<RouteLocationNormalizedLoaded, 'fullPath'>,
	toRecordedUrl: (url: string) => string = url => url
): void {
	setLastSrpUrlInStorage(toRecordedUrl(route.fullPath));
}
